import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItemBase,
    DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { TableAction } from '@/types/table';
import { Button } from '@/components/ui/Button';

interface ActionsCellProps<TData> {
    row: TData;
    actions: TableAction<TData>[];
}

export function ActionsCell<TData>({ row, actions }: ActionsCellProps<TData>) {
    const [isLoading, setIsLoading] = useState(false);

    const visibleActions = actions.filter((action) => {
        if (action.show === undefined) return true;
        return action.show;
    });

    if (visibleActions.length === 0) return null;

    const handleAction = async (action: TableAction<TData>) => {
        setIsLoading(true);
        try {
            await action.onClick(row);
        } catch (error) {
            console.error('Action failed:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="small"
                        disabled={isLoading}
                        leadingIcon={<MoreHorizontal className="h-4 w-4" />}
                    />
                </DropdownMenuTrigger>

                <DropdownMenuContent
                    variant="compact"
                    side="bottom"
                    align="end"
                    sideOffset={5}
                >
                    <DropdownMenuGroup>
                        {visibleActions.map((action, index) => (
                            <DropdownMenuItemBase
                                key={index}
                                variant={
                                    action.variant === 'danger'
                                        ? 'danger'
                                        : 'default'
                                }
                                disabled={isLoading}
                                onSelect={() => {
                                    void handleAction(action);
                                }}
                            >
                                {action.icon && (
                                    <span className="flex-shrink-0">
                                        {action.icon}
                                    </span>
                                )}
                                <span className="text-sm font-medium">
                                    {action.label}
                                </span>
                            </DropdownMenuItemBase>
                        ))}
                    </DropdownMenuGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}
